/**
 * CelebrationGlyph — MS284 Editorial Glyph Morph (Recognitions, Direction B).
 *
 * Direct port of the design handoff at
 * `lembra-mascot/project/Editorial glyph morph - Recognitions.html`
 * (received 2026-05-18). The new five-frame sequence reads as
 * "we remembered" — the full job of a recognition, start to finish:
 *
 *   ① Speech bubble    → ② Calendar     → ③ Stockpot
 *   → ④ Greeting card → ⑤ Desk-drop    → ① …
 *
 * Each glyph pairs 1:1 with a verb (see LEMBRA_VERB_VOCAB.recognitionsMorph
 * in ThinkingStrip.tsx). The glyph + verb share the exact same opacity
 * timeline (pair-cycle keyframe in index.css), so frame 1 + verb 1 fade
 * in together, hold ~1.7s, then cross-fade out together. Lucky's "in
 * sync and flush" goal is enforced at the animation timeline, not at
 * the rendering layer.
 *
 * Technical pattern (preserved from the prior MS57.5 / MS280 morph):
 *   - All five frames live in ONE SVG, no DOM churn.
 *   - Single 10s keyframe + staggered animation-delay values
 *     (0s, -8s, -6s, -4s, -2s) cycles them — same pattern
 *     the design's pair-cycle implements.
 *   - Stroke uses currentColor so the parent ThinkingStrip can
 *     control hue via a Tailwind text-* utility.
 *   - 24×24 viewBox; holds from 20px inline strip to 240px brand
 *     moment without swapping artwork.
 *   - prefers-reduced-motion override in index.css pins to frame 1.
 *
 * Two stroke weights match the design's .ink / .ink-thin convention:
 *   - 1.5px primary for structural lines (the bubble outline, the
 *     calendar page, the pot body, the card frame, the package box)
 *   - 1.1px @ 70% opacity for inner detail (the ribbon bands on the
 *     package, the cursive lines on the card, the steam wisps from
 *     the pot)
 *
 * Fills only where the mark needs weight (bubble dots, calendar
 * date tile, package bow knot — same convention as the design).
 *
 * Path data is copied verbatim from the design SVG symbols.
 */

interface CelebrationGlyphMorphProps {
  /** Glyph size in pixels (square). Default 14 to match the
   *  ThinkingStrip default-density size. Holds 16-240px without
   *  losing legibility per the design spec. */
  size?: number;
  /** Tailwind colour class applied to the wrapping span so all the
   *  SVG strokes (which use currentColor) inherit the right hue.
   *  Typically text-navy/70 for the chat surfaces; the dark
   *  concierge pill in the design uses cream — that's just a
   *  different colorClass at the call site. */
  colorClass?: string;
  /** Extra positioning classes on the wrapper. */
  className?: string;
}

export function CelebrationGlyphMorph({
  size = 14,
  colorClass = "text-navy/70",
  className = "",
}: CelebrationGlyphMorphProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center ${colorClass} ${className}`}
      aria-hidden="true"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        // overflow:visible so 1.5px strokes near the edge of the
        // viewBox don't get clipped on render.
        style={{ overflow: "visible" }}
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* ① Speech bubble — chat balloon with three filled dots.
            The "we heard you" beat. Paired with "Considering your
            feedback." */}
        <g className="glyph-frame glyph-frame-1">
          <path d="M 4 5 Q 4 3.4 5.6 3.4 L 18.4 3.4 Q 20 3.4 20 5 L 20 13.4 Q 20 15 18.4 15 L 12.4 15 L 9.4 18.6 L 9.4 15 L 5.6 15 Q 4 15 4 13.4 Z" />
          <circle cx={9}  cy={9.2} r={0.85} fill="currentColor" stroke="none" />
          <circle cx={12} cy={9.2} r={0.85} fill="currentColor" stroke="none" />
          <circle cx={15} cy={9.2} r={0.85} fill="currentColor" stroke="none" />
        </g>

        {/* ② Calendar — page, header divider, two binding hangers
            at the top, one filled rounded date tile off-centre right.
            Lucky's design-review note: the filled-square date marker
            is what stops it reading as a camera lens. Paired with
            "Marking the day." */}
        <g className="glyph-frame glyph-frame-2">
          <rect x={3.5} y={6}   width={17} height={14.5} rx={1.6} />
          <line x1={3.5} y1={9.6} x2={20.5} y2={9.6} />
          <line x1={8.5}  y1={3.8} x2={8.5}  y2={7} />
          <line x1={15.5} y1={3.8} x2={15.5} y2={7} />
          <rect x={12.5} y={13} width={5} height={5} rx={0.8}
            fill="currentColor" stroke="none" />
        </g>

        {/* ③ Stockpot — pot body + slim rim + small hook handles +
            spoon angling out + two thin steam wisps. Paired with
            "Cooking up options." */}
        <g className="glyph-frame glyph-frame-3">
          {/* Body */}
          <path d="M 5 13 L 6 19.5 Q 6 20.5 7 20.5 L 17 20.5 Q 18 20.5 18 19.5 L 19 13" />
          {/* Rim */}
          <line x1={4} y1={13} x2={20} y2={13} />
          {/* Hook handles — small quarter-arcs on each side */}
          <path d="M 4 13.6 Q 3 13.6 3 14.8 Q 3 16 4 16" />
          <path d="M 20 13.6 Q 21 13.6 21 14.8 Q 21 16 20 16" />
          {/* Spoon — shaft + tilted bowl angling up-right */}
          <line x1={13} y1={13} x2={17.3} y2={5.5} />
          <ellipse cx={18} cy={4.4} rx={1.5} ry={0.95}
            transform="rotate(-58 18 4.4)" />
          {/* Steam wisps — thin-weight inner detail */}
          <path d="M 7.2 11.2 Q 8 9 7.2 6.8 Q 6.4 4.6 7.2 2.6"
            strokeWidth={1.1} opacity={0.7} />
          <path d="M 10 11.2 Q 10.8 9.4 10 7.6"
            strokeWidth={1.1} opacity={0.7} />
        </g>

        {/* ④ Greeting card — folded card outline with two cursive
            handwriting lines (thin-weight). Paired with "Getting
            thoughtful." */}
        <g className="glyph-frame glyph-frame-4">
          <rect x={4.4} y={5} width={15.2} height={14} rx={1} />
          <path d="M 7.4 10.8 Q 9 9.6 10.5 10.8 T 13.5 10.8 T 16.6 10.8"
            strokeWidth={1.1} opacity={0.7} />
          <path d="M 7.4 14 Q 9 12.8 10.5 14 T 13.5 14"
            strokeWidth={1.1} opacity={0.7} />
        </g>

        {/* ⑤ Desk-drop — package with bow sitting on a horizontal
            surface. The closing beat — "the gift just landed."
            Paired with "Almost there." */}
        <g className="glyph-frame glyph-frame-5">
          {/* Desk surface line */}
          <line x1={2.4} y1={19.4} x2={21.6} y2={19.4} />
          {/* Package body */}
          <rect x={7.6} y={10.6} width={8.8} height={8.8} rx={0.4} />
          {/* Ribbon — vertical + horizontal bands, thin-weight */}
          <line x1={12}  y1={10.6} x2={12}  y2={19.4}
            strokeWidth={1.1} opacity={0.7} />
          <line x1={7.6} y1={14}   x2={16.4} y2={14}
            strokeWidth={1.1} opacity={0.7} />
          {/* Bow loops — two C-shapes meeting at the top of the package */}
          <path d="M 12 10.6 Q 9.6 9 9.8 7.2 Q 11.4 7.8 12 9.8" />
          <path d="M 12 10.6 Q 14.4 9 14.2 7.2 Q 12.6 7.8 12 9.8" />
        </g>
      </svg>
    </span>
  );
}
